import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../components/DynamicIcon";
import { deleteWarehouse, getWarehousesList, resetSelectedWarehouse, selectWarehousesList, selectWarehousesLoading } from "../redux/warehouse/warehouseSlice";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import LoadingSection from "../components/LoadingSection";
import DynamicTitle from "../components/DynamicTitle";
import WarehouseCard from "../components/warehousePage/WarehouseCard";
import RightModal from "../components/templates/RightModal";
import AddEditWarehouse from "../components/warehousePage/AddEditWarehouse";
import ViewWarehouse from "../components/warehousePage/ViewWarehouse";
import WarehouseProducts from "../components/warehousePage/WarehouseProducts";

export default function WarehouseList() {
    const dispatch = useDispatch();
    const warehousesList = useSelector(selectWarehousesList);
    const warehousesLoading = useSelector(selectWarehousesLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalWarehouseId = useSelector(selectRightModalId);

    useEffect(() => {
        if (accountData?._id) {
            dispatch(getWarehousesList({ accountId: accountData._id, token }));
        }
    }, [dispatch, accountData]);

    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow && accountData?._id) {
            dispatch(getWarehousesList({ accountId: accountData._id, token }));
            dispatch(resetRefreshNow());
        }
    }, [rightModalIsRefreshNow, dispatch, accountData]);

    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetSelectedWarehouse());
        }
    }, [rightModalIsOpen, dispatch]);

    // Stato modale conferma eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [warehouseToDelete, setWarehouseToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteClick = (warehouse) => {
        setWarehouseToDelete(warehouse);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!warehouseToDelete) return;
        setIsDeleting(true);
        try {
            await dispatch(deleteWarehouse({ warehouseId: warehouseToDelete._id, token })).unwrap();
            setShowDeleteModal(false);
            setWarehouseToDelete(null);
        } catch (error) {
            alert(`Errore durante l'eliminazione: ${error}`);
        } finally {
            setIsDeleting(false);
        }
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setWarehouseToDelete(null);
    };

    const handleAddWarehouse = () => {
        dispatch(resetSelectedWarehouse());
        dispatch(setModal({ mode: 'add', percentuageWidth: 45 }));
    };

    return (
        <div className="page">
            {warehousesLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Magazzini" />

            <h1>Magazzini</h1>

            <div className="flex justify-end mb-4">
                <button className="primary" onClick={handleAddWarehouse}>
                    <DynamicIcon iconClass="fa-solid fa-plus" />
                    Nuovo Magazzino
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {warehousesList && warehousesList.length > 0 ? (
                    warehousesList.map((warehouse) => (
                        <WarehouseCard key={warehouse._id} warehouse={warehouse} onDelete={handleDeleteClick} />
                    ))
                ) : !warehousesLoading ? (
                    <div className="col-span-full text-center text-gray-500 py-8">
                        Nessun magazzino disponibile. Crea il tuo primo magazzino!
                    </div>
                ) : null}
            </div>

            {/* MODALE CONFERMA ELIMINAZIONE */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">
                            Conferma eliminazione magazzino
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Sei sicuro di voler eliminare il magazzino <strong>{warehouseToDelete?.name}</strong>?
                            <br />
                            <span className="text-sm text-yellow-700 mt-2 block">
                                I prodotti associati non verranno eliminati, ma perderanno il collegamento a questo magazzino.
                            </span>
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={closeDeleteModal}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                disabled={isDeleting}
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Eliminazione...' : 'Elimina'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rightModalIsOpen && (
                <RightModal>
                    {rightModalMode === 'add' && (
                        <AddEditWarehouse mode="add" />
                    )}
                    {rightModalMode === 'edit' && rightModalWarehouseId && (
                        <AddEditWarehouse mode="edit" />
                    )}
                    {rightModalMode === 'view' && rightModalWarehouseId && (
                        <ViewWarehouse />
                    )}
                    {rightModalMode === 'products' && rightModalWarehouseId && (
                        <WarehouseProducts warehouseId={rightModalWarehouseId} />
                    )}
                </RightModal>
            )}
        </div>
    );
}
